import { useQuery } from '@tanstack/react-query';
import { CloudOff, FlaskConical, Info, KeyRound, ShieldAlert } from 'lucide-react';
import { type FormEvent, type ReactNode, useState } from 'react';
import { ApiError, getSignInProvider } from '../lib/api';
import { forgotPassword, getSignInOptions, signInWithPassword } from '../lib/accounts';
import { describeKeyError, signInWithKey, webauthnAvailable } from '../lib/webauthn';
import { Button, buttonClass } from '../ui/Button';
import { cn } from '../ui/cn';
import { Logo } from '../ui/misc';
import { rememberReturnPath, type SignedOutReason } from './session';

const messages: Record<Exclude<SignedOutReason, null>, { tone: 'info' | 'warn'; text: string }> = {
  expired: { tone: 'info', text: 'Your session ended. Sign in again to pick up where you left off.' },
  'signed-out': { tone: 'info', text: 'You’ve signed out.' },
  denied: { tone: 'warn', text: 'That account isn’t set up for WITTCO RMM. Ask an owner to add you, then try again.' },
  failed: { tone: 'warn', text: 'Sign-in didn’t complete. Please try again.' },
  cancelled: { tone: 'info', text: 'Sign-in was cancelled.' },
  unavailable: { tone: 'warn', text: 'Sign-in is busy right now. Wait a minute and try again.' },
};

export function AuthFrame({ children }: { children: ReactNode }) {
  return (
    <div className="relative flex min-h-dvh items-center justify-center overflow-hidden px-4 py-12">
      <div aria-hidden className="pointer-events-none absolute inset-0 [background:radial-gradient(60%_50%_at_50%_0%,var(--c-brand-soft),transparent_70%)]" />
      <div aria-hidden className="pointer-events-none absolute inset-0 opacity-[0.35] [background-image:linear-gradient(var(--c-line)_1px,transparent_1px),linear-gradient(90deg,var(--c-line)_1px,transparent_1px)] [background-size:44px_44px] [mask-image:radial-gradient(ellipse_at_center,black_20%,transparent_70%)]" />
      <main className="relative w-full max-w-[400px] animate-rise">{children}</main>
    </div>
  );
}

function GoogleMark() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden className="size-[18px]">
      <path fill="#4285F4" d="M22.5 12.27c0-.79-.07-1.54-.2-2.27H12v4.3h5.9a5.05 5.05 0 0 1-2.2 3.3v2.77h3.55c2.08-1.92 3.25-4.74 3.25-8.1z" />
      <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.56-2.76c-.98.66-2.24 1.06-3.72 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A11 11 0 0 0 12 23z" />
      <path fill="#FBBC05" d="M5.84 14.1A6.6 6.6 0 0 1 5.5 12c0-.73.13-1.44.34-2.1V7.07H2.18A11 11 0 0 0 1 12c0 1.78.43 3.45 1.18 4.93l3.66-2.84z" />
      <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15A10.96 10.96 0 0 0 12 1 11 11 0 0 0 2.18 7.07l3.66 2.84C6.71 7.3 9.14 5.38 12 5.38z" />
    </svg>
  );
}

/** `onFirstFactor`: a password was accepted; the gate then shows the second factor. */
export function SignedOut({ reason, onFirstFactor }: { reason: SignedOutReason; onFirstFactor?: () => void }) {
  const message = reason ? messages[reason] : null;
  const provider = useQuery({ queryKey: ['sign-in-provider'], queryFn: ({ signal }) => getSignInProvider(signal), staleTime: Infinity }).data;
  const local = useQuery({ queryKey: ['sign-in-options'], queryFn: ({ signal }) => getSignInOptions(signal), staleTime: Infinity }).data?.localAccounts;
  return (
    <AuthFrame>
      <div className="rounded-2xl border border-line bg-panel/95 p-8 shadow-pop backdrop-blur">
        <Logo className="size-10" />
        <h1 className="mt-6 text-[22px] font-semibold tracking-tight">Sign in to WITTCO RMM</h1>
        <p className="mt-1.5 text-ink-2">Find any customer or device, see what needs a look, and ask questions in plain language.</p>
        {message && (
          <p role="status" className={cn('mt-5 flex items-start gap-2 rounded-lg px-3 py-2.5 text-[13px]',
            message.tone === 'warn' ? 'bg-attention-soft text-attention' : 'bg-sunken text-ink-2')}>
            {message.tone === 'warn' ? <ShieldAlert className="mt-px size-4 shrink-0" /> : <Info className="mt-px size-4 shrink-0" />}
            <span>{message.text}</span>
          </p>
        )}
        {/* A full navigation: the server owns the sign-in state cookie and redirect. */}
        <a href="/auth/login" onClick={rememberReturnPath}
          className={buttonClass('secondary', 'md', 'mt-6 h-11 w-full justify-center text-[15px]')}>
          {provider === 'synthetic' ? <><FlaskConical className="size-4 text-attention" /> Continue with test sign-in</>
            : provider === 'google' ? <><GoogleMark /> Continue with Google</> : 'Sign in'}
        </a>
        <KeySignIn />
        {local && <PasswordSignIn onFirstFactor={onFirstFactor} />}
        <p className="mt-4 text-center text-[12.5px] text-ink-3">{provider === 'synthetic'
          ? 'Local synthetic runner: a test identity signs you in to invented demo data. Google isn’t contacted.'
          : local ? 'Staff use Google; customers use the email and password they set up. Access is by invitation only.'
          : 'Use your company Google Workspace account. Access is by invitation only.'}</p>
      </div>
    </AuthFrame>
  );
}

const field = 'h-10 w-full rounded-lg border border-line bg-panel px-3 text-[14px] outline-none focus:border-brand/50';

/**
 * An email and password (WRMM-134): a first factor only; the second step follows. "Forgot your password?" sends a reset
 * link if the address has an account, and says the same either way.
 */
function PasswordSignIn({ onFirstFactor }: { onFirstFactor?: () => void }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [forgot, setForgot] = useState(false);
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true); setError(null);
    try {
      if (forgot) { await forgotPassword(email.trim()); setSent(true); }
      else { await signInWithPassword(email.trim(), password); rememberReturnPath(); onFirstFactor?.(); }
    } catch (failure) {
      setError(failure instanceof ApiError && failure.status === 429 ? 'Too many attempts. Wait a minute and try again.'
        : forgot ? 'That didn’t go through. Try again.' : 'That email and password don’t match an account. After ten tries, it locks for 15 minutes.');
    } finally { setBusy(false); }
  };
  return (
    <form onSubmit={e => void submit(e)} className="mt-5 border-t border-line pt-5">
      <label className="block text-[13px] font-medium" htmlFor="sign-in-email">Email</label>
      <input id="sign-in-email" type="email" autoComplete="username" className={cn(field, 'mt-1')} value={email} onChange={e => setEmail(e.target.value)} maxLength={254} />
      {!forgot && <>
        <label className="mt-3 block text-[13px] font-medium" htmlFor="sign-in-password">Password</label>
        <input id="sign-in-password" type="password" autoComplete="current-password" className={cn(field, 'mt-1')} value={password} onChange={e => setPassword(e.target.value)} maxLength={1024} />
      </>}
      {sent ? <p role="status" className="mt-3 text-[13px] text-ink-2">If that address has an account, a reset link is on its way. It works for 30 minutes.</p>
        : <Button type="submit" className="mt-3 h-10 w-full justify-center" disabled={busy || !email.trim() || (!forgot && !password)}>
          {busy ? 'Checking…' : forgot ? 'Send a reset link' : 'Sign in with email'}</Button>}
      {error && <p role="alert" className="mt-2 text-[12.5px] text-attention">{error}</p>}
      <button type="button" className="mt-2 w-full text-center text-[12.5px] text-ink-3 underline-offset-2 hover:underline"
        onClick={() => { setForgot(!forgot); setSent(false); setError(null); }}>{forgot ? 'Back to sign-in' : 'Forgot your password?'}</button>
    </form>
  );
}

/** For when Google sign-in isn't available: any approval key the person registered signs them in. */
function KeySignIn() {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  if (!webauthnAvailable()) return null;
  const run = async () => {
    setBusy(true); setError(null);
    try {
      await signInWithKey();
      rememberReturnPath();
      window.location.assign('/');
    } catch (e) {
      setBusy(false);
      const name = (e as { name?: string })?.name;
      setError(name === 'NotAllowedError' ? 'No key was used. If yours isn’t offered, it may be set up for approvals only: Settings shows which of your keys can sign you in.'
        : !(e instanceof ApiError) ? describeKeyError(e)
        : e.status === 403 ? 'That key’s owner has no active access to WITTCO RMM.'
        : e.status === 429 ? 'Too many attempts. Wait a minute and try again.'
        : 'That key isn’t registered for WITTCO RMM, or it was removed.');
    }
  };
  return (
    <>
      <Button variant="ghost" className="mt-2 h-10 w-full justify-center" disabled={busy} onClick={() => void run()}>
        <KeyRound className="size-4" />{busy ? 'Waiting for your key…' : 'Use a security key or passkey'}
      </Button>
      {error && <p role="alert" className="mt-1 text-center text-[12.5px] text-attention">{error}</p>}
    </>
  );
}

export function Splash() {
  return (
    <div className="flex min-h-dvh items-center justify-center" aria-busy="true" aria-label="Loading WITTCO RMM">
      <Logo className="size-9 animate-pulse" />
    </div>
  );
}

export function BootError({ onRetry }: { onRetry: () => void }) {
  return (
    <AuthFrame>
      <div role="alert" className="rounded-2xl border border-line bg-panel p-8 text-center shadow-pop">
        <CloudOff className="mx-auto size-8 text-ink-3" />
        <h1 className="mt-4 text-lg font-semibold">Can’t reach WITTCO RMM</h1>
        <p className="mt-1 text-ink-2">Your session couldn’t be checked. Check your connection and try again.</p>
        <Button className="mt-5" onClick={onRetry}>Try again</Button>
      </div>
    </AuthFrame>
  );
}

